import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Looked up when asked for, not when this module loads.
 *
 * Reading it at load time captures whatever `window` held on the first
 * import, which misses anything installed later and makes the hook
 * impossible to exercise without a real browser recogniser.
 */
const getRecognition = () =>
  typeof window === "undefined"
    ? null
    : window.SpeechRecognition || window.webkitSpeechRecognition || null;

export const isDictationSupported = () => Boolean(getRecognition());

/** What went wrong, in words a student can act on. */
const REASONS = {
  "not-allowed": "Microphone access was blocked. Allow it in your browser and try again.",
  "service-not-allowed": "Microphone access was blocked. Allow it in your browser and try again.",
  "no-speech": "Nothing was heard. Try again, a little closer to the microphone.",
  "audio-capture": "No microphone was found.",
  network: "Dictation needs a connection and could not reach the service.",
  aborted: null, // the student stopped it; not an error
};

/**
 * Speaking an answer instead of typing it.
 *
 * Uses the browser's own recogniser rather than recording audio and sending
 * it somewhere: the words appear as they are spoken instead of after a round
 * trip, nothing leaves for a server this app runs, and there is no per-minute
 * cost. The trade is that it is a Chrome, Edge and Safari feature — Firefox
 * has no implementation — so the button says so rather than failing silently.
 *
 * Only finalised phrases are committed. The recogniser re-sends its working
 * guess on every result, so appending everything it says would repeat each
 * phrase several times; `resultIndex` is what marks the ones it has settled
 * on, and everything before it has already been handed over.
 */
export function useDictation({ onCommit, lang = "en-GB" } = {}) {
  const [supported] = useState(() => Boolean(getRecognition()));
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const wantedRef = useRef(false);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const stop = useCallback(() => {
    wantedRef.current = false;
    setListening(false);
    setInterim("");
    try {
      recognitionRef.current?.stop();
    } catch {
      /* already stopped */
    }
  }, []);

  const start = useCallback(() => {
    const Recognition = getRecognition();
    if (!Recognition) {
      setError("Dictation is not available in this browser. Chrome, Edge and Safari support it.");
      return;
    }
    setError(null);
    wantedRef.current = true;

    const recognition = new Recognition();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let settled = "";
      let pending = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) settled += result[0].transcript;
        else pending += result[0].transcript;
      }
      setInterim(pending);
      if (settled.trim()) commitRef.current?.(settled);
    };

    recognition.onerror = (event) => {
      const reason = REASONS[event.error];
      if (reason) setError(reason);
      // A hard failure should not be retried in a loop.
      if (event.error !== "no-speech") wantedRef.current = false;
    };

    recognition.onend = () => {
      setInterim("");
      // The recogniser stops itself after a pause even when continuous; if
      // the student has not pressed stop, pick it straight back up.
      if (wantedRef.current) {
        try {
          recognition.start();
          return;
        } catch {
          /* fall through to stopping */
        }
      }
      setListening(false);
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setListening(true);
    } catch (err) {
      setError("Dictation could not start.");
      wantedRef.current = false;
    }
  }, [lang]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  // Leaving the page with the microphone open would keep it open.
  useEffect(() => () => {
    wantedRef.current = false;
    try {
      recognitionRef.current?.abort();
    } catch {
      /* nothing to abort */
    }
  }, []);

  return {
    supported,
    listening,
    interim,
    error,
    clearError: useCallback(() => setError(null), []),
    start,
    stop,
    toggle,
  };
}
